import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  TextField, Switch, Stack, Chip, Select, MenuItem, FormControl, InputLabel,
  Alert, FormHelperText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import useConfirm from '../calendar/dialogs/UseConfirm';
import { hasAnyRequiredPermission, WORKSHIFT_LOCAL_WRITE_PERMISSIONS } from '../../config/routePermissions';
import { useSnackbar } from 'notistack';

export default function WorkshiftLocalConfigPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, ConfirmDialog } = useConfirm();
  const theme = useTheme();
  const { permissions } = useContext(UserContext);
  const canRead = permissions.includes('workshifts.read');
  const canWrite = hasAnyRequiredPermission(permissions, WORKSHIFT_LOCAL_WRITE_PERMISSIONS);

  const [loading, setLoading] = useState(false);
  const [showDisabled, setShowDisabled] = useState(true);

  const [stations, setStations] = useState([]);
  const [rows, setRows] = useState([]); // {station_id, station_name, id, name, hour_from, ...}

  // crear
  const [createOpen, setCreateOpen] = useState(false);
  const [cStationId, setCStationId] = useState('');
  const [cName, setCName] = useState('');
  const [cFrom, setCFrom] = useState('08:00');
  const [cTo, setCTo] = useState('16:00');
  const [cNight, setCNight] = useState(false);
  const [cDesc, setCDesc] = useState('');
  const [cHasBreak, setCHasBreak] = useState(false);
  const [cBreakFrom, setCBreakFrom] = useState('12:00');
  const [cBreakTo, setCBreakTo] = useState('15:00');
  const [createError, setCreateError] = useState('');
  const [createFieldErrors, setCreateFieldErrors] = useState({});

  // editar (turno base)
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [eName, setEName] = useState('');
  const [eFrom, setEFrom] = useState('08:00');
  const [eTo, setETo] = useState('16:00');
  const [eNight, setENight] = useState(false);
  const [eDesc, setEDesc] = useState('');
  const [eHasBreak, setEHasBreak] = useState(false);
  const [eBreakFrom, setEBreakFrom] = useState('12:00');
  const [eBreakTo, setEBreakTo] = useState('15:00');
  const [editError, setEditError] = useState('');

  const fmtHHMM = v => (v || '').slice(0, 5);

  const fmtRange = (row) => {
    const from = fmtHHMM(row.hour_from);
    const to = fmtHHMM(row.hour_to);
    if (row.break_from && row.break_to) {
      return `${from}–${fmtHHMM(row.break_from)} / ${fmtHHMM(row.break_to)}–${to}`;
    }
    return `${from}–${to}`;
  };

  const validateBreakRange = (hasBreak, from, to, bFrom, bTo) => {
    if (!hasBreak) return null;
    if (!bFrom || !bTo) return 'Debe indicar el horario de descanso completo';
    if (!(from < bFrom && bFrom < bTo && bTo < to)) {
      return 'El descanso debe estar dentro del horario del turno (Desde < Descanso desde < Descanso hasta < Hasta)';
    }
    return null;
  };

  // ---------- Cargas ----------
  const fetchStations = async () => {
    try {
      const stRes = await axios.get('station');
      setStations(stRes.data || []);
    } catch {
      console.warn('No se pudo cargar la lista de estaciones');
    }
  };

  const fetchLocals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/workshifts?local=true&include_disabled=${showDisabled}`);
      const list = (res.data || []);

      list.sort((a, b) => {
        if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
        if (a.station_name !== b.station_name) return a.station_name.localeCompare(b.station_name);
        return (a.hour_from || '').localeCompare(b.hour_from || '');
      });

      setRows(list);
    } catch {
      enqueueSnackbar('Error al listar turnos locales', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showDisabled, enqueueSnackbar]);

  useEffect(() => {
    if (canWrite) {
      fetchStations();
    }
  }, [canWrite]);

  useEffect(() => {
    if (canRead) {
      fetchLocals();
    }
  }, [canRead, fetchLocals]);

  // ---------- Validación: turno duplicado en la misma estación ----------
  const isDuplicateName = (stationId, name, excludeId = null) => {
    return rows.some(row => 
      String(row.station_id) === String(stationId) &&
      row.name.trim().toLowerCase() === name.trim().toLowerCase() &&
      (excludeId === null || row.id !== excludeId)
    );
  };

  // ---------- Crear local (catálogo + asociación) ----------
  const openCreate = () => {
    setCStationId('');
    setCName('');
    setCFrom('08:00');
    setCTo('16:00');
    setCNight(false);
    setCDesc('');
    setCHasBreak(false);
    setCBreakFrom('12:00');
    setCBreakTo('15:00');
    setCreateError('');
    setCreateFieldErrors({});
    setCreateOpen(true);
  };

  const saveCreate = async () => {
    setCreateError('');

    const fieldErrors = {};
    if (!cStationId) fieldErrors.station = 'Debe seleccionar una estación.';
    if (!cName.trim()) fieldErrors.name = 'El nombre del turno es obligatorio.';
    else if (cStationId && isDuplicateName(cStationId, cName)) {
      fieldErrors.name = `Ya existe un turno llamado "${cName.trim()}" en esta estación.`;
    }
    setCreateFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setCreateError('Revisá los campos marcados en rojo.');
      return;
    }

    const breakError = validateBreakRange(cHasBreak, cFrom, cTo, cBreakFrom, cBreakTo);
    if (breakError) {
      setCreateError(breakError);
      return;
    }

    try {
      // 1) crear turno no global
      const p1 = new URLSearchParams({
        name: cName.trim(),
        hour_from: cFrom,
        hour_to: cTo,
        is_night: String(cNight),
        enabled: 'true',
        description: cDesc.trim(),
        is_global: 'false',
      });
      if (cHasBreak) {
        p1.set('break_from', cBreakFrom);
        p1.set('break_to', cBreakTo);
      }
      const created = await axios.post(`/workshift?${p1.toString()}`);
      const ws = created.data;

      // 2) asociarlo a la estación
      const p2 = new URLSearchParams({ workshift_id: String(ws.id), is_principal: 'false' });
      await axios.post(`/station/${cStationId}/workshifts?${p2.toString()}`);

      setCreateOpen(false);
      fetchLocals();
    } catch (error) {
      setCreateError(error.response?.data?.error || error.response?.data?.message || 'Error al crear turno local');
    }
  };

  // ---------- Editar turno base ----------
  const openEdit = (row) => {
    setEditing(row);
    setEName(row.name || '');
    setEFrom(fmtHHMM(row.hour_from || '08:00'));
    setETo(fmtHHMM(row.hour_to || '16:00'));
    setENight(!!row.is_night);
    setEDesc(row.description || '');
    setEHasBreak(Boolean(row.break_from && row.break_to));
    setEBreakFrom(fmtHHMM(row.break_from || '12:00'));
    setEBreakTo(fmtHHMM(row.break_to || '15:00'));
    setEditError('');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditError('');

    if (!editing) return;

    const normalizedName = eName.trim().toLowerCase();
    const originalName = String(editing.name || '').trim().toLowerCase();

    if (!eName.trim()) {
      setEditError('El nombre del turno es obligatorio');
      return;
    }

    // Validar duplicado solo si cambió el nombre
    if (normalizedName !== originalName && isDuplicateName(editing.station_id, eName, editing.id)) {
      setEditError(`Ya existe otro turno llamado "${eName.trim()}" en esta estación`);
      return;
    }

    const breakError = validateBreakRange(eHasBreak, eFrom, eTo, eBreakFrom, eBreakTo);
    if (breakError) {
      setEditError(breakError);
      return;
    }

    try {
      const params = new URLSearchParams({
        name: eName.trim(),
        hour_from: eFrom,
        hour_to: eTo,
        is_night: String(eNight),
        enabled: String(editing.enabled),
        description: eDesc.trim(),
        is_global: 'false',
      });
      if (eHasBreak) {
        params.set('break_from', eBreakFrom);
        params.set('break_to', eBreakTo);
      } else {
        params.set('break_from', '');
        params.set('break_to', '');
      }
      await axios.patch(`/workshift/${editing.id}?${params.toString()}`);
      setEditOpen(false);
      setEditing(null);
      fetchLocals();
    } catch (error) {
      setEditError(error.response?.data?.error || error.response?.data?.message || 'Error al guardar cambios del turno');
    }
  };

  // ---------- Habilitar/Deshabilitar asociación ----------
  const disableAssoc = async (row) => {
    const ok = await confirm({
      title: 'Deshabilitar turno',
      content: `¿Seguro que querés deshabilitar el turno "${row.name}" en la estación "${row.station_name}"?`,
      confirmText: 'Deshabilitar',
    });
    if (!ok) return;
    try {
      await axios.patch(`/station/${row.station_id}/workshifts/${row.id}?enabled=false`);
      fetchLocals();
    } catch {
      enqueueSnackbar('Error al deshabilitar en la estación', { variant: 'error' });
    }
  };

  const enableAssoc = async (row) => {
    try {
      await axios.patch(`/station/${row.station_id}/workshifts/${row.id}?enabled=true`);
      fetchLocals();
    } catch {
      enqueueSnackbar('Error al habilitar en la estación', { variant: 'error' });
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="error">No tienes permiso para ver turnos locales.</Typography>
      </Box>
    );
  }

  return (
    <>
      {ConfirmDialog}
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="h4" align="center">Turnos Locales</Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
          Turnos asociados a una única estación.
        </Typography>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, px: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">Mostrar deshabilitados</Typography>
            <Switch checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
          </Stack>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Agregar turno local
            </Button>
          )}
        </Stack>
      </Box>

      {/* Tabla */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 1100 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Horario</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Estación</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && rows.map(row => (
                <TableRow key={`${row.station_id}-${row.id}`} sx={!row.enabled ? { backgroundColor: '#f7f7f7' } : {}}>

                  <TableCell sx={!row.enabled ? { color: '#999', textDecoration: 'line-through' } : {}}>
                    {row.name}
                  </TableCell>

                  <TableCell sx={!row.enabled ? { color: '#999' } : {}}>
                    {fmtRange(row)}
                  </TableCell>

                  <TableCell>
                    {row.is_night
                      ? <Chip size="small" label="Nocturno" />
                      : <Chip size="small" label="Diurno" variant="outlined" />
                    }
                  </TableCell>

                  <TableCell sx={!row.enabled ? { color: '#999' } : {}}>
                    {row.description || '—'}
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={row.station_name}
                      sx={{
                        bgcolor: alpha(theme.palette.warning.main, 0.18),
                        color: theme.palette.warning.dark,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                        fontWeight: 600,
                        maxWidth: 200,
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    {row.enabled
                      ? <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#2e7d32', fontWeight: 600 }}>
                          <CheckCircleIcon fontSize="small" /> Habilitado
                        </Stack>
                      : <span style={{ color: '#999', fontWeight: 600 }}>Deshabilitado</span>
                    }
                  </TableCell>

                  <TableCell align="right">
                    {canWrite && (
                      <>
                        <IconButton size="small" onClick={() => openEdit(row)} title="Editar turno (base)">
                          <EditIcon />
                        </IconButton>
                        {row.enabled ? (
                          <IconButton size="small" onClick={() => disableAssoc(row)} title="Deshabilitar en esta estación">
                            <BlockIcon />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => enableAssoc(row)} title="Habilitar en esta estación">
                            <RestoreIcon />
                          </IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {loading && (
                <TableRow><TableCell colSpan={7}>Cargando…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7}>No hay turnos locales para mostrar.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Modal crear */}
      <Dialog open={createOpen && canWrite} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Agregar turno local</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {createError && (
              <Alert severity="error" onClose={() => setCreateError('')}>
                {createError}
              </Alert>
            )}
            
            <FormControl fullWidth required error={Boolean(createFieldErrors.station)}>
              <InputLabel id="station-select-label">Estación</InputLabel>
              <Select
                labelId="station-select-label"
                value={cStationId}
                label="Estación"
                onChange={e => { setCStationId(e.target.value); setCreateFieldErrors(p => ({ ...p, station: undefined })); }}
              >
                {stations.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
              {createFieldErrors.station && <FormHelperText>{createFieldErrors.station}</FormHelperText>}
            </FormControl>
            <TextField
              label="Nombre"
              required
              value={cName}
              onChange={e => { setCName(e.target.value); setCreateFieldErrors(p => ({ ...p, name: undefined })); }}
              inputProps={{ maxLength: 20 }}
              error={Boolean(createFieldErrors.name)}
              helperText={createFieldErrors.name}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField label="Desde" type="time" value={cFrom} onChange={e => setCFrom(e.target.value)} inputProps={{ step: 300 }} fullWidth />
              <TextField label="Hasta" type="time" value={cTo} onChange={e => setCTo(e.target.value)} inputProps={{ step: 300 }} fullWidth />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>¿Nocturno?</Typography>
              <Switch checked={cNight} onChange={e => setCNight(e.target.checked)} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>¿Turno cortado (con descanso)?</Typography>
              <Switch checked={cHasBreak} onChange={e => setCHasBreak(e.target.checked)} />
            </Stack>
            {cHasBreak && (
              <Stack direction="row" spacing={2}>
                <TextField label="Descanso desde" type="time" value={cBreakFrom} onChange={e => setCBreakFrom(e.target.value)} inputProps={{ step: 300 }} fullWidth />
                <TextField label="Descanso hasta" type="time" value={cBreakTo} onChange={e => setCBreakTo(e.target.value)} inputProps={{ step: 300 }} fullWidth />
              </Stack>
            )}
            <TextField label="Descripción" value={cDesc} onChange={e => setCDesc(e.target.value)} inputProps={{ maxLength: 100 }} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveCreate}>Crear y asociar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal editar */}
      <Dialog open={editOpen && canWrite} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar turno local</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError('')}>
                {editError}
              </Alert>
            )}
            
            {editing && (
              <TextField label="Estación (asociación)" value={editing.station_name} fullWidth InputProps={{ readOnly: true }} />
            )}
            <TextField label="Nombre" value={eName} onChange={e => setEName(e.target.value)} inputProps={{ maxLength: 20 }} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Desde" type="time" value={eFrom} onChange={e => setEFrom(e.target.value)} inputProps={{ step: 300 }} fullWidth />
              <TextField label="Hasta" type="time" value={eTo} onChange={e => setETo(e.target.value)} inputProps={{ step: 300 }} fullWidth />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>¿Nocturno?</Typography>
              <Switch checked={eNight} onChange={e => setENight(e.target.checked)} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>¿Turno cortado (con descanso)?</Typography>
              <Switch checked={eHasBreak} onChange={e => setEHasBreak(e.target.checked)} />
            </Stack>
            {eHasBreak && (
              <Stack direction="row" spacing={2}>
                <TextField label="Descanso desde" type="time" value={eBreakFrom} onChange={e => setEBreakFrom(e.target.value)} inputProps={{ step: 300 }} fullWidth />
                <TextField label="Descanso hasta" type="time" value={eBreakTo} onChange={e => setEBreakTo(e.target.value)} inputProps={{ step: 300 }} fullWidth />
              </Stack>
            )}
            <TextField label="Descripción" value={eDesc} onChange={e => setEDesc(e.target.value)} inputProps={{ maxLength: 100 }} fullWidth />
            <Typography variant="caption" color="text.secondary">
              * Editás el turno base (no global). Si está asociado a más estaciones, también verán el cambio.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveEdit}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
